import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Text,
  Link,
  Header,
  Button,
  List,
  ListItem,
  ListItemKey,
  ListItemValue,
  Loader,
} from '@pet-notify/ui'

import type { ActivateInput } from 'services/orders'
import { useOrderByActivationCode } from 'hooks/orders'
import { usePlans } from 'hooks/plans'
import style from './style.module.css'
import { formatMoney } from '@pet-notify/formatters'

type Props = {
  state: Partial<ActivateInput>
  onSubmit: () => void
}

function Confirmation({ state, onSubmit }: Props) {
  const orderQuery = useOrderByActivationCode(state.activationCode)
  const plansQuery = usePlans()

  const plan = useMemo(() => {
    if (plansQuery.data && state.planInterval) {
      return plansQuery.data.find((p) => p.interval === state.planInterval)
    }
  }, [plansQuery, state.planInterval])

  if (orderQuery.isLoading || !orderQuery.data || plansQuery.isLoading) {
    return <Loader />
  }

  return (
    <>
      <Header mb='6' size='large'>
        Intellitag Activation &amp; Plan Confirmation
      </Header>
      <div className={style.headerWithLink}>
        <Header size='medium' as='h3'>
          Account summary
        </Header>
        <Link as={RouterLink} to='/activate/2'>
          Edit account
        </Link>
      </div>

      <List>
        <ListItem>
          <ListItemKey>
            <Text mb='1'>
              <strong>Name:</strong> {state.firstName} {state.lastName}
            </Text>

            <Text mb='1'>
              <strong>Phone:</strong> {state.phone}
            </Text>

            <Text mb='1'>
              <strong>Email:</strong> {state.email}
            </Text>

            <Text mb='1'>
              <strong>Password:</strong>{' '}
              {state.password &&
                state.password
                  .split('')
                  .map(() => '*')
                  .join('')}
            </Text>

            <Text mb='1'>
              <strong>Activation Code:</strong> {state.activationCode}
            </Text>

            <Text mb='1'>
              <strong>Pet(s):</strong>{' '}
              {orderQuery.data.intellitagOrderItems
                .map((item) => item.pet?.name)
                .join(', ')}
            </Text>
          </ListItemKey>
        </ListItem>
      </List>

      <div className={style.headerWithLink}>
        <Header size='medium' as='h3'>
          Intellitag with pet alarms monitoring plan
        </Header>
        <Link as={RouterLink} to='/activate/2/plan'>
          Edit plan
        </Link>
      </div>

      {plan && (
        <List>
          <ListItem>
            <ListItemKey>
              Paid {plan.interval === 'year' ? 'Annually' : 'Monthly'}
            </ListItemKey>
            <ListItemValue>
              {plan.interval === 'year' ? (
                <>{formatMoney(Math.floor(plan.amount / 12) / 100)} per month</>
              ) : (
                <>{formatMoney(plan.amount / 100)} per month</>
              )}
            </ListItemValue>
          </ListItem>
        </List>
      )}

      <Button onClick={onSubmit} mb='6'>
        Confirm
      </Button>

      <Text className={style.text}>
        Have a question? Email us at{' '}
        <Link href='mailto:support@petnotify.com'>support@petnotify.com</Link>.
      </Text>
    </>
  )
}

export default Confirmation
